import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { cx } from '@libs/classnames';
import { Form } from '@ui/components/Form';
import { GoldenModal } from '@ui/components/GoldenModal/GoldenModal';
import { ThemeButton } from '@ui/components/ThemeButton';

import { ClubSponsorsInput } from '@features/club/ui/ClubFormInput/ClubSponsorsInput';
import { DescriptionInput } from '@features/club/ui/ClubFormInput/DescriptionInput';
import { LogoInput } from '@features/club/ui/ClubFormInput/LogoInput';
import { TelegramInput } from '@features/club/ui/ClubFormInput/TelegramInput';
import { ThemeColorInput } from '@features/club/ui/ClubFormInput/ThemeColorInput';
import { TitleInput } from '@features/club/ui/ClubFormInput/TitleInput';

import styles from './ClubInfoForm.module.css';

type UpdatableClubSponsor = {
  number: number;
  caption?: string | null;
  text?: string | null;
  logoUri?: string | null;
  linkUri?: string | null;
};

export type UpdateClubFormValues = {
  title: string;
  description: string;
  telegramChannel: string;
  themeColor: string | null;
  groupTitle: string | null;
  logoUrl?: string | null;
  sponsors?: UpdatableClubSponsor[] | null;
};

export type Props = {
  className?: string;
  club: UpdateClubFormValues;
  onSubmit: (newClub: UpdateClubFormValues) => Promise<UpdateClubFormValues>;
  onUploadLogo: (formData: FormData) => Promise<unknown>;
  onUploadSponsorLogo: (sponsorNumber: number, file: FormData) => Promise<unknown>;
  disabled?: boolean;
};

export function ClubInfoForm({
  className,
  club,
  onSubmit,
  onUploadLogo,
  onUploadSponsorLogo,
  disabled,
}: Props) {
  const [successModal, setSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm<UpdateClubFormValues>({
    disabled: disabled || isSubmitting,
    defaultValues: club,
  });

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(methods.getValues());
      setSuccessModal(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [onSubmit, methods]);

  const onCancel = useCallback(() => {
    methods.reset();
  }, [methods]);

  useEffect(() => {
    if (successModal) {
      setTimeout(() => setSuccessModal(false), 3000);
    }
  }, [successModal]);

  useEffect(() => {
    methods.resetField('logoUrl', { defaultValue: club.logoUrl });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [club.logoUrl]);

  useEffect(() => {
    methods.resetField('sponsors', { defaultValue: club.sponsors });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [club.sponsors]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit}>
        <Form className={cx(className, styles.root)}>
          <TitleInput invisible />
          <DescriptionInput />
          <TelegramInput />
          <LogoInput onUpload={onUploadLogo} />
          <ClubSponsorsInput onUploadSponsorLogo={onUploadSponsorLogo} />
          <ThemeColorInput />

          <div className={styles.buttonBar}>
            <ThemeButton inverted onClick={onCancel}>
              Отмена
            </ThemeButton>
            <ThemeButton
              onClick={handleSubmit}
              disabled={!methods.formState.isValid || disabled || isSubmitting}
            >
              Сохранить
            </ThemeButton>

            {successModal && (
              <GoldenModal className={styles.modal}>Данные клуба успешно сохранены</GoldenModal>
            )}
          </div>
        </Form>
      </form>
    </FormProvider>
  );
}
